import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { agentsData } from "Stores/agentsData";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import SignalIcon from "./SignalIcon";
import { familyOf, costText, isSignal } from "./signalMeta";

const ASKS = [
  "Tell me when my customers change jobs",
  "Watch my top 20 accounts for headcount growth",
  "Who from my ICP is going to an event this month?"
];

// ["a", "b", "c"] -> "a, b and c"
export const listOf = xs => (xs.length > 1 ? `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}` : xs.join(""));

/** Before anything is tracked: what is already on and three good first picks. No empty stat tiles or example rows on day one. */
const SignalsFirstVisit = ({ onTrack, onBrowse }) => {
  const history = useHistory();
  const signals = signalsData.catalog.filter(isSignal);
  const fam = d => familyOf(d.category).key;
  const byId = id => signals.find(d => d.id === id);
  const own = signals.filter(d => fam(d) === "own");
  // The server has switched the free signals on: an empty feed means a quiet fortnight, not nothing set up.
  const freeOn = signals.some(d => d.alwaysOnSignalId);
  // The list they already built (onboarding saves one) makes the first pick concrete.
  useEffect(() => {
    agentsData.fetchContactLists();
  }, []);
  const ts = v => (typeof v === "number" ? v : Date.parse(v) || 0);
  // The newest list with people in it: usually the one the user just built.
  const list = (agentsData.contactLists || [])
    .filter(l => l.count > 0)
    .sort((a, b) => ts(b.createTs) - ts(a.createTs))[0];
  const starters = [
    list
      ? { key: "job", def: byId("job_change") || signals.find(d => fam(d) === "ppl"), title: `Job changes on ${list.name}`, tag: "Recommended",
          line: `${Number(list.count).toLocaleString()} people. Hear when any of them changes jobs.`, listId: list.id }
      : { key: "job", def: byId("job_change") || signals.find(d => fam(d) === "ppl"), title: "Job changes", tag: "Recommended",
          line: "When someone you have contacted changes jobs, you hear first and keep the account covered." },
    { key: "acc", def: byId("hiring_intel") || signals.find(d => fam(d) === "acc"), title: "Hiring intel",
      line: "See what your accounts are hiring for, the tech and skills they need, and who is hiring, so you can reach them." },
    { key: "champ", def: signals.find(d => /champion/.test(d.id || "")), title: "Champion tracker", tag: "Needs a customers list",
      line: "Your past customers moving to a new company are your warmest new deals. Point it at your customers list." }
  ];

  return (
    <div>
      {freeOn && <p className="sg-quietline">Nothing in the last 14 days. We'll show the next one here.</p>}

      {own.length > 0 && (
        <div className="sg-freebie">
          <span className="sg-tile own"><SignalIcon id="bolt" /></span>
          <div className="sg-grow">
            <b>Already on, and free</b>
            <p>{listOf(own.map(d => d.label))} watch your sequences for you. New ones land here as they happen.</p>
          </div>
          <span className="ag-pill ag-pill--live"><i className="ag-dot" /> On</span>
        </div>
      )}

      <div className="sg-freebie">
        <span className="sg-tile cmp"><SignalIcon id="competitors" /></span>
        <div className="sg-grow">
          <b>Your competitors</b>
          <p>From your seller profile. The teams they're building, what they ship and where they're headed.</p>
        </div>
        <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => history.push("/signals/competitors")}>See competitors</button>
      </div>

      <div className="sg-trackhead"><h3>Start with one</h3></div>
      <div className="sg-starters">
        {starters.map(s => {
          const f = s.def ? fam(s.def) : s.key === "champ" ? "ppl" : "acc";
          return (
            <div key={s.key} className="sg-starter">
              <div className="sg-starter__head">
                <span className={`sg-tile ${f}`}><SignalIcon id={s.def ? s.def.id : "user"} /></span>
                <b>{s.title}</b>
                {s.tag && <span className={`sg-pill ${s.key === "job" ? "sg-pill--rec" : "sg-pill--soon"}`}>{s.tag}</span>}
              </div>
              <p>{s.line}</p>
              <div className="sg-starter__cost"><SignalIcon id="coin" size={14} />{s.def ? costText(s.def) : "1 credit per contact, each check"}</div>
              {s.def ? (
                <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => onTrack(s.def, s.listId)}><SignalIcon id="plus" size={14} /> Track</button>
              ) : s.key === "champ" ? (
                <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={() => history.push("/settings/crm")}>Connect your CRM</button>
              ) : (
                <button className="sg-btn sg-btn--small sg-btn--ghost" onClick={onBrowse}>See signal templates</button>
              )}
            </div>
          );
        })}
      </div>

      <div className="sg-ask">
        <div className="sg-grow">
          <b>Or just ask</b>
          <p>Tell Copilot what you want to hear about. It picks the signal, shows the cost and starts tracking after your OK.</p>
          <div className="sg-ask__list">{ASKS.map(a => <span key={a}>“{a}”</span>)}</div>
        </div>
        <button className="sg-btn sg-btn--ghost" onClick={() => copilotDrawerStore.openFor("signals")}><SignalIcon id="sparkle" size={16} /> Ask Copilot</button>
      </div>
    </div>
  );
};

export default observer(SignalsFirstVisit);
